import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb';
import { Decimals, TimeInterval } from '../api/common_pb';
import { TimeUnit } from '../api/enum_pb';
import { GetKlinesReply, GetKlinesRequest } from '../api/market_pb';
import { MarketServiceClient } from '../api/MarketServiceClientPb';
import { Bar, ResolutionString } from '../lib/charting-library';
import BlockchainMapper from '../mappers/BlockchainMapper';
import Blockchain from '../models/Blockchain.enum';

function convertResolutionToInterval(resolution: ResolutionString): TimeInterval | null {
  if (!resolution) {
    return null;
  }

  const resolutionStringRegExp = /^([0-9]+)([A-Z]?)$/g;

  const parsedResolution = resolutionStringRegExp.exec(resolution);

  if (!parsedResolution) {
    return null;
  }

  const [, value, unit] = parsedResolution;

  if (!value) {
    return null;
  }

  const result = new TimeInterval();

  result.setValue(Number.parseInt(value));

  switch (unit) {
    case 'S': {
      result.setUnit(TimeUnit.TIMEUNIT_SECONDS);
      break;
    }
    case 'D': {
      result.setUnit(TimeUnit.TIMEUNIT_DAYS);
      break;
    }
    case 'W': {
      result.setUnit(TimeUnit.TIMEUNIT_WEEKS);
      break;
    }
    case 'M': {
      result.setUnit(TimeUnit.TIMEUNIT_MONTHS);
      break;
    }
    default: {
      result.setUnit(TimeUnit.TIMEUNIT_MINUTES);
      break;
    }
  }

  return result;
}

function convertDecimalsToNumber(decimals: Decimals | undefined): number {
  if (!decimals) {
    return 0;
  }

  return decimals.getValue() * Math.pow(10, decimals.getExp());
}

export class MarketDataService {
  private readonly url: string;
  private readonly client: MarketServiceClient;

  constructor(url?: string) {
    this.url = url ?? process.env.REACT_APP_MARKETDATA_API_URL;

    this.client = new MarketServiceClient(this.url);
  }

  getKLines = async (
    blockchain: Blockchain,
    tokenAddress: string | null,
    poolAddress: string | null,
    resolution: ResolutionString,
    startTime?: number | undefined,
    endTime?: number | undefined,
    limit?: number | undefined,
  ): Promise<Bar[]> => {
    const request = new GetKlinesRequest();
    request.setChain(BlockchainMapper.toDto(blockchain));

    if (tokenAddress) {
      request.setTokenaddress(tokenAddress);
    }

    if (poolAddress) {
      request.setPooladdress(poolAddress);
    }

    const interval = convertResolutionToInterval(resolution);

    if (interval) {
      request.setInterval(interval);
    }

    if (startTime) {
      const startTimeValue = new Timestamp();
      startTimeValue.setSeconds(startTime);

      request.setStarttime(startTimeValue);
    } else {
      const now = Date.now();

      let delta = 0;

      switch (interval!.getUnit()) {
        case TimeUnit.TIMEUNIT_SECONDS: {
          delta = interval!.getValue();
          break;
        }
        case TimeUnit.TIMEUNIT_MINUTES: {
          delta = interval!.getValue() * 60;
          break;
        }
        case TimeUnit.TIMEUNIT_HOURS: {
          delta = interval!.getValue() * 60 * 60;
          break;
        }
        case TimeUnit.TIMEUNIT_DAYS: {
          delta = interval!.getValue() * 60 * 24;
          break;
        }
        case TimeUnit.TIMEUNIT_WEEKS: {
          delta = interval!.getValue() * 60 * 24 * 7;
          break;
        }
        case TimeUnit.TIMEUNIT_MONTHS: {
          delta = interval!.getValue() * 60 * 24 * 31;
          break;
        }
      }

      const startTimeMillis = now - delta * 1000;

      const startTimeSeconds = Math.round(startTimeMillis / 1000);

      const startTimeValue = new Timestamp();
      startTimeValue.setSeconds(startTimeSeconds);

      request.setStarttime(startTimeValue);
    }

    if (endTime) {
      const endTimeValue = new Timestamp();
      endTimeValue.setSeconds(endTime);

      request.setEndtime(endTimeValue);
    } else {
      const now = Date.now();

      const endTimeValue = new Timestamp();
      endTimeValue.setSeconds(Math.round(now / 1000));

      request.setEndtime(endTimeValue);
    }

    if (limit) {
      request.setLimit(limit);
    }

    const response: GetKlinesReply = await this.client.getKlines(request);

    const result = response.getResult();

    console.log({
      request: request.toObject(true),
      response: response.toObject(true),
      // requestJSON: JSON.stringify(request.toObject(true)),
      // responseJSON: JSON.stringify(response.toObject(true)),
    });

    if (!result?.getIssuccess()) {
      throw new Error(result?.getErrorsList().join(', '));
    }

    const kLines = response.getKlinesList();

    return kLines.map((kLine) => ({
      time: (kLine.getOpentime()?.getSeconds() ?? 0) * 1000,
      low: convertDecimalsToNumber(kLine.getLowprice()),
      high: convertDecimalsToNumber(kLine.getHighprice()),
      open: convertDecimalsToNumber(kLine.getOpenprice()),
      close: convertDecimalsToNumber(kLine.getCloseprice()),
      volume: convertDecimalsToNumber(kLine.getVolumebase()),
    }));
  }
}

const service = new MarketDataService();

export default service;
